'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Award,
  BookOpen,
  Calendar,
  Check,
  ChevronRight,
  CircleUserRound,
  X,
  Clock3,
  ExternalLink,
  FileCheck2,
  Film,
  HeartHandshake,
  Image as ImageIcon,
  MapPin,
  MessageCircle,
  Pencil,
  Play,
  Share2,
  ShieldCheck,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import { LOCALE_BCP47, type UILocale } from '@/i18n/LanguageContext';
import {
  LifeSoundtrackCollection,
  LifeSoundtrackHeader,
} from '@/components/public-profile/LifeSoundtrack';
import type {
  PublicFamilyItem,
  PublicProfilePayload,
  PublicProofCard,
  PublicPulsePost,
  PublicRecord,
  PublicStory,
} from '@/lib/public-profile/types';
import { PublicCareerAndChapters, PublicCurrentRole } from '@/components/public-profile/PublicLifeRecordContext';

type ProfileTab =
  | 'overview'
  | 'timeline'
  | 'stories'
  | 'media'
  | 'achievements'
  | 'journey'
  | 'about';

type TimelineKind = 'story' | 'achievement' | 'journey' | 'family';

type TimelineItem = {
  id: string;
  kind: TimelineKind;
  title: string;
  date: string;
  summary: string | null;
  location: string | null;
  mediaUrl: string | null;
  verified: boolean;
  witnessed: boolean;
};

type MediaItem = {
  id: string;
  title: string;
  url: string;
  type: 'photo' | 'video';
  context: 'story' | 'achievement';
  href: string;
};

type Props = {
  data: PublicProfilePayload;
  routeHandle: string;
  isOwnerView: boolean;
  viewAsPublic: boolean;
  viewerSignedIn: boolean;
  authLoading: boolean;
  isFollowing: boolean;
  followLoading: boolean;
  copied: boolean;
  publicVaultId: string;
  publicVaultHref: string;
  onFollow: () => void;
  onMessage: () => void;
  onShare: () => void;
  onTogglePublicView: () => void;
  onRequestSection?: (section: ProfileTab) => void;
  loadingSection?: ProfileTab | null;
};

type ProfileEmphasis = 'stories' | 'recognition' | 'journey' | 'family' | 'balanced';

const TAB_ORDER: ProfileTab[] = [
  'overview',
  'timeline',
  'stories',
  'media',
  'achievements',
  'journey',
  'about',
];

const FAMILY_LABEL_KEYS = {
  relationship_self: 'public_profile.family_relationship_self',
  relationship_parent: 'public_profile.family_relationship_parent',
  relationship_grandparent: 'public_profile.family_relationship_grandparent',
  relationship_great_grandparent: 'public_profile.family_relationship_great_grandparent',
  relationship_child: 'public_profile.family_relationship_child',
  relationship_sibling: 'public_profile.family_relationship_sibling',
  relationship_spouse: 'public_profile.family_relationship_spouse',
  relationship_aunt_uncle: 'public_profile.family_relationship_aunt_uncle',
  relationship_cousin: 'public_profile.family_relationship_cousin',
  relationship_guardian: 'public_profile.family_relationship_guardian',
  relationship_other: 'public_profile.family_relationship_other',
  record_photo: 'public_profile.family_record_photo',
  record_document: 'public_profile.family_record_document',
  record_certificate: 'public_profile.family_record_certificate',
  record_story: 'public_profile.family_record_story',
  record_migration: 'public_profile.family_record_migration',
  record_education: 'public_profile.family_record_education',
  record_career: 'public_profile.family_record_career',
  record_milestone: 'public_profile.family_record_milestone',
  record_audio: 'public_profile.family_record_audio',
  record_video: 'public_profile.family_record_video',
  record_object: 'public_profile.family_record_object',
  record_memorial: 'public_profile.family_record_memorial',
  record_record: 'public_profile.family_record_other',
} as const;

const KIND_STYLE: Record<TimelineKind, { icon: typeof BookOpen; color: string; wash: string }> = {
  story: { icon: BookOpen, color: '#6552a5', wash: '#f1edff' },
  achievement: { icon: Award, color: '#9a5b13', wash: '#fff4d8' },
  journey: { icon: MapPin, color: '#087c71', wash: '#e3f7f3' },
  family: { icon: Users, color: '#a13e63', wash: '#fcebf2' },
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEAR_ONLY_PATTERN = /^\d{4}$/;

function calendarSafeDate(date: string): Date {
  if (YEAR_ONLY_PATTERN.test(date)) return new Date(Date.UTC(Number(date), 0, 1, 12));
  const dateOnly = DATE_ONLY_PATTERN.exec(date);
  if (dateOnly) {
    return new Date(
      Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 12)
    );
  }
  return new Date(date);
}

function displayDate(date: string, locale: UILocale): string {
  if (!date) return '';
  if (YEAR_ONLY_PATTERN.test(date)) return date;
  const parsed = calendarSafeDate(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(LOCALE_BCP47[locale], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

function yearOf(date: string): string {
  if (!date) return '—';
  if (YEAR_ONLY_PATTERN.test(date)) return date;
  const dateOnly = DATE_ONLY_PATTERN.exec(date);
  if (dateOnly) return dateOnly[1];
  const parsed = calendarSafeDate(date);
  return Number.isNaN(parsed.getTime()) ? '—' : String(parsed.getUTCFullYear());
}

function dateSortValue(date: string): number {
  const parsed = calendarSafeDate(date);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function requestedScrollBehavior(smooth: boolean): ScrollBehavior {
  if (!smooth || typeof window === 'undefined') return 'auto';
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|webm|avi|mkv)(\?.*)?$/i.test(url) || /\/video/i.test(url);
}

function isPhotoUrl(url: string): boolean {
  return /\.(jpe?g|png|gif|webp|avif|heic)(\?.*)?$/i.test(url) || /\/(photo|image)/i.test(url);
}

function storyIsVideo(story: PublicStory): boolean {
  return story.content_type === 'video' || Boolean(story.media_url && isVideoUrl(story.media_url));
}

function storyIsPhoto(story: PublicStory): boolean {
  return story.content_type === 'photo' || Boolean(story.media_url && isPhotoUrl(story.media_url));
}

function meaningfulStory(stories: PublicStory[]): PublicStory | null {
  return (
    [...stories].sort((a, b) => {
      const score = (story: PublicStory) =>
        (story.media_url ? 5 : 0) +
        (story.milestone_date ? 3 : 0) +
        Math.min((story.body?.trim().length || 0) / 80, 4) +
        Math.min(story.witness_count || 0, 3);
      return score(b) - score(a);
    })[0] || null
  );
}

function meaningfulRecord(records: PublicRecord[]): PublicRecord | null {
  return (
    [...records].sort((a, b) => {
      const score = (record: PublicRecord) =>
        (record.verification_state === 'institution_issued'
          ? 8
          : record.verification_state === 'institution_reviewed'
            ? 6
            : 0) +
        (record.witness_state === 'witnessed' ? 4 : 0) +
        (record.evidence_state === 'added' ? 3 : 0) +
        (record.media.length > 0 ? 2 : 0) +
        (record.description ? 1 : 0);
      return score(b) - score(a);
    })[0] || null
  );
}

function storyExcerpt(body: string | null, maxLength = 230): string | null {
  const normalized = body?.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  const sentence = normalized.match(/^.*?[.!?](?:\s|$)/)?.[0]?.trim();
  const excerpt = sentence && sentence.length >= 48 ? sentence : normalized;
  if (excerpt.length <= maxLength) return excerpt;
  return `${excerpt.slice(0, maxLength).replace(/\s+\S*$/, '')}…`;
}

function profileEmphasis(data: PublicProfilePayload): ProfileEmphasis {
  const journeyCount = data.stats.publicJourneyMoments ?? data.journeyMoments.length;
  const summarizedFamilyCount =
    data.familySummary.public_family_members_count +
    data.familySummary.public_family_records_count +
    (data.familySummary.public_heritage_items_count ??
      data.familyItems.filter((item) => item.kind === 'heritage').length);
  const familyCount = summarizedFamilyCount > 0 ? summarizedFamilyCount : data.familyItems.length;
  const scores: Array<[Exclude<ProfileEmphasis, 'balanced'>, number]> = [
    [
      'stories',
      data.stats.publicStories * 2 + (data.stories.some((story) => story.media_url) ? 3 : 0),
    ],
    [
      'recognition',
      data.stats.publicRecords * 2 +
        data.stats.publicProofCards * 3 +
        data.stats.verifiedRecords * 4 +
        data.records.filter((record) => record.witness_state === 'witnessed').length * 2,
    ],
    ['journey', journeyCount * 3],
    ['family', familyCount * 3],
  ];
  const ordered = scores.sort((a, b) => b[1] - a[1]);
  if (!ordered[0] || ordered[0][1] === 0 || ordered[0][1] === ordered[1]?.[1]) return 'balanced';
  return ordered[0][0];
}

function recordTrust(record: PublicRecord) {
  if (record.verification_state === 'institution_issued') return 'institution';
  if (record.verification_state === 'institution_reviewed') return 'verified';
  if (record.witness_state === 'witnessed') return 'witnessed';
  if (record.evidence_state === 'added') return 'evidence';
  return 'personal';
}

function buildTimeline(data: PublicProfilePayload): TimelineItem[] {
  const stories: TimelineItem[] = data.visibility.show_stories
    ? data.stories.map((story) => ({
        id: `story-${story.id}`,
        kind: 'story',
        title: story.title || '',
        date: story.milestone_date || story.created_at,
        summary: story.body,
        location: null,
        mediaUrl: story.media_url,
        verified: false,
        witnessed: story.witness_count > 0,
      }))
    : [];

  const achievements: TimelineItem[] = data.visibility.show_records
    ? data.records.map((record) => ({
        id: `achievement-${record.id}`,
        kind: 'achievement',
        title: record.title,
        date: record.achievement_date || '',
        summary: record.description,
        location: record.location?.name || null,
        mediaUrl: record.media[0]?.file_url || null,
        verified: ['institution_reviewed', 'institution_issued'].includes(
          record.verification_state
        ),
        witnessed: record.witness_state === 'witnessed',
      }))
    : [];

  const journey: TimelineItem[] = data.journeyMoments.map((moment) => ({
    id: `journey-${moment.id}`,
    kind: 'journey',
    title: moment.title,
    date: moment.date,
    summary: moment.summary,
    location: moment.location_name,
    mediaUrl: moment.thumbnail_url,
    verified: false,
    witnessed: false,
  }));

  const family: TimelineItem[] = data.visibility.show_family_preview
    ? data.familyItems.flatMap((item) =>
        item.timeline_date
          ? [
              {
                id: `family-${item.id}`,
                kind: 'family' as const,
                title: item.title,
                date: item.timeline_date,
                summary: item.description || item.subtitle || null,
                location: null,
                mediaUrl: item.thumbnail_url || null,
                verified: false,
                witnessed: false,
              },
            ]
          : []
      )
    : [];

  return [...stories, ...achievements, ...journey, ...family].sort((a, b) => {
    const aTime = dateSortValue(a.date || '');
    const bTime = dateSortValue(b.date || '');
    return aTime - bTime;
  });
}

function selectTimelinePreview(items: TimelineItem[], limit = 4): TimelineItem[] {
  const recent = [...items].sort((a, b) => dateSortValue(b.date) - dateSortValue(a.date));
  const selected: TimelineItem[] = [];

  for (const kind of ['story', 'achievement', 'journey', 'family'] as const) {
    const item = recent.find((candidate) => candidate.kind === kind);
    if (item) selected.push(item);
  }

  for (const item of recent) {
    if (selected.length >= limit) break;
    if (!selected.some((candidate) => candidate.id === item.id)) selected.push(item);
  }

  return selected.slice(0, limit).sort((a, b) => dateSortValue(a.date) - dateSortValue(b.date));
}

function buildMedia(data: PublicProfilePayload): MediaItem[] {
  const storyMedia = data.stories.flatMap<MediaItem>((story) => {
    if (!story.media_url) return [];
    const type = storyIsVideo(story) ? 'video' : storyIsPhoto(story) ? 'photo' : null;
    if (!type) return [];
    return [
      {
        id: `story-${story.id}`,
        title: story.title || '',
        url: story.media_url,
        type,
        context: 'story',
        href: `/community?story=${encodeURIComponent(story.id)}`,
      },
    ];
  });
  const achievementMedia = data.records.flatMap<MediaItem>((record) =>
    record.media.flatMap((media) => {
      const type =
        /video/i.test(media.media_type) || isVideoUrl(media.file_url) ? 'video' : 'photo';
      if (
        type === 'photo' &&
        !isPhotoUrl(media.file_url) &&
        !/image|photo/i.test(media.media_type)
      ) {
        return [];
      }
      return [
        {
          id: `record-${record.id}-${media.id}`,
          title: media.caption || record.title,
          url: media.file_url,
          type,
          context: 'achievement',
          href: '#achievements',
        },
      ];
    })
  );
  return [...storyMedia, ...achievementMedia];
}

function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="lr-section-heading">
      <div>
        {eyebrow && <p className="lr-eyebrow">{eyebrow}</p>}
        <h2>{title}</h2>
        {description && <p className="lr-section-description">{description}</p>}
      </div>
      {action}
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof BookOpen;
  title: string;
  body: string;
}) {
  return (
    <div className="lr-empty">
      <span className="lr-empty-icon">
        <Icon size={20} />
      </span>
      <div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </div>
  );
}

function TimelineList({ items, compact = false }: { items: TimelineItem[]; compact?: boolean }) {
  const { t, locale } = useTranslation();
  return (
    <ol className={`lr-timeline ${compact ? 'lr-timeline-compact' : ''}`}>
      {items.map((item, index) => {
        const visual = KIND_STYLE[item.kind];
        const Icon = visual.icon;
        const year = yearOf(item.date);
        const startsYear = index === 0 || yearOf(items[index - 1]?.date || '') !== year;
        return (
          <li
            key={item.id}
            className="lr-timeline-item"
            id={startsYear && year !== '—' ? `lr-year-${year}` : undefined}
          >
            <div className="lr-timeline-year">{startsYear ? year : ''}</div>
            <div className="lr-timeline-line" aria-hidden>
              <span />
            </div>
            <article className="lr-timeline-card">
              <div className="lr-timeline-copy">
                <div className="lr-card-meta">
                  <span
                    className="lr-kind"
                    style={{ color: visual.color, background: visual.wash }}
                  >
                    <Icon size={12} /> {t(`public_profile.filter_${item.kind}`)}
                  </span>
                  {item.date && (
                    <span>
                      <Calendar size={12} /> {displayDate(item.date, locale)}
                    </span>
                  )}
                  {item.location && (
                    <span>
                      <MapPin size={12} /> {item.location}
                    </span>
                  )}
                </div>
                <h3>{item.title || t('public_profile.untitled_story')}</h3>
                {item.summary && <p>{item.summary}</p>}
                {(item.verified || item.witnessed) && (
                  <div className="lr-trust-row">
                    {item.verified && (
                      <span>
                        <ShieldCheck size={13} /> {t('public_profile.verified_record')}
                      </span>
                    )}
                    {item.witnessed && (
                      <span>
                        <HeartHandshake size={13} /> {t('public_profile.witnessed_records')}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {item.mediaUrl && (
                <div className="lr-timeline-media">
                  <img src={item.mediaUrl} alt={item.title} loading="lazy" />
                </div>
              )}
            </article>
          </li>
        );
      })}
    </ol>
  );
}

function StoryGrid({
  stories,
  emptyName: _emptyName,
}: {
  stories: PublicStory[];
  emptyName: string;
}) {
  const { t, locale } = useTranslation();
  if (stories.length === 0) {
    return (
      <EmptyState
        icon={BookOpen}
        title={t('empty_states.no_stories')}
        body={t('public_profile.no_stories_body')}
      />
    );
  }
  return (
    <div className="lr-story-grid">
      {stories.map((story, index) => (
        <article
          className={`lr-story-card ${index === 0 ? 'lr-story-featured' : ''} ${
            index === 0 && !story.media_url ? 'lr-story-featured-text' : ''
          }`}
          key={story.id}
        >
          {story.media_url && (
            <div className="lr-story-media">
              {storyIsVideo(story) ? (
                <video
                  src={story.media_url}
                  preload="metadata"
                  aria-label={story.title || t('public_profile.untitled_story')}
                />
              ) : (
                <img
                  src={story.media_url}
                  alt={story.title || t('public_profile.untitled_story')}
                  loading="lazy"
                />
              )}
            </div>
          )}
          <div className="lr-story-copy">
            <div className="lr-card-meta">
              {story.pillar_tag && (
                <span className="lr-kind lr-kind-neutral">{story.pillar_tag}</span>
              )}
              <span>
                <Calendar size={12} />{' '}
                {displayDate(story.milestone_date || story.created_at, locale)}
              </span>
              {story.witness_count > 0 && (
                <span>
                  <HeartHandshake size={12} /> {t('public_profile.witnessed_records')}
                </span>
              )}
            </div>
            <h3>{story.title || t('public_profile.untitled_story')}</h3>
            {story.body &&
              (index === 0 ? (
                <blockquote>“{storyExcerpt(story.body, 320)}”</blockquote>
              ) : (
                <p>{storyExcerpt(story.body, 190)}</p>
              ))}
            <Link
              href={`/community?story=${encodeURIComponent(story.id)}`}
              className="lr-text-link"
            >
              {t('trust.view_story')} <ChevronRight size={14} />
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}

function AchievementGrid({
  records,
  proofCards = [],
  emptyName: _emptyName,
}: {
  records: PublicRecord[];
  proofCards?: PublicProofCard[];
  emptyName: string;
}) {
  const { t, locale } = useTranslation();
  if (records.length === 0 && proofCards.length === 0) {
    return (
      <EmptyState
        icon={Award}
        title={t('empty_states.no_achievements')}
        body={t('public_profile.no_achievements_body')}
      />
    );
  }
  return (
    <div className="lr-recognition-stack">
      {records.length > 0 && (
        <div className="lr-achievement-grid">
          {records.map((record) => {
            const trust = recordTrust(record);
            const trustLabel =
              trust === 'institution'
                ? t('public_profile.institution_issued')
                : trust === 'verified'
                  ? t('public_profile.institution_recognized')
                  : trust === 'witnessed'
                    ? t('public_profile.witnessed_records')
                    : trust === 'evidence'
                      ? t('public_profile.evidence_attached')
                      : t('public_profile.personal_record');
            return (
              <article className="lr-achievement-card" key={record.id}>
                <div className="lr-achievement-icon">
                  <Award size={20} />
                </div>
                <div className="lr-card-meta">
                  <span className={`lr-trust-badge lr-trust-${trust}`}>
                    <ShieldCheck size={12} /> {trustLabel}
                  </span>
                  {record.achievement_date && (
                    <span>{displayDate(record.achievement_date, locale)}</span>
                  )}
                </div>
                <h3>{record.title}</h3>
                {(record.institution || record.pillar) && (
                  <p className="lr-achievement-byline">
                    {[record.institution, record.pillar].filter(Boolean).join(' · ')}
                  </p>
                )}
                {record.description && <p>{record.description}</p>}
                <div className="lr-evidence-row">
                  {record.evidence_state === 'added' && (
                    <span>
                      <FileCheck2 size={13} /> {t('public_profile.evidence_attached')}
                    </span>
                  )}
                  {record.witness_state === 'witnessed' && (
                    <span>
                      <HeartHandshake size={13} /> {t('public_profile.witnessed_records')}
                    </span>
                  )}
                  {record.location?.name && (
                    <span>
                      <MapPin size={13} /> {record.location.name}
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {proofCards.length > 0 && (
        <div className="lr-proof-list">
          {proofCards.map((proof) => (
            <article className="lr-proof-row" key={proof.id}>
              {proof.thumbnail_url ? (
                <img src={proof.thumbnail_url} alt="" loading="lazy" />
              ) : (
                <span className="lr-proof-icon" aria-hidden>
                  <ShieldCheck size={18} />
                </span>
              )}
              <div>
                <span className="lr-kind lr-trust-verified">
                  <ShieldCheck size={12} /> {t('dashboard.trust_record')}
                </span>
                <h3>{proof.title}</h3>
                <p>
                  {[
                    proof.issuer_name,
                    proof.issue_date ? displayDate(proof.issue_date, locale) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function FamilyPreview({ items, name }: { items: PublicFamilyItem[]; name: string }) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <p className="lr-muted-copy">
        {t('public_profile.no_family_body').replace('{{name}}', name)}
      </p>
    );
  }
  return (
    <div className="lr-family-list">
      {items.slice(0, 3).map((item, index) => {
        const labelTranslationKey = item.label_key
          ? FAMILY_LABEL_KEYS[item.label_key as keyof typeof FAMILY_LABEL_KEYS]
          : null;
        return (
          <div
            className={`lr-family-item ${index === 0 ? 'lr-family-item-featured' : ''}`}
            key={item.id}
          >
            {item.thumbnail_url ? (
              <img src={item.thumbnail_url} alt={item.title} loading="lazy" />
            ) : (
              <span>
                <CircleUserRound size={18} />
              </span>
            )}
            <div>
              <h4>{item.title}</h4>
              <p>
                {(labelTranslationKey ? t(labelTranslationKey) : item.relationship_label) ||
                  item.subtitle ||
                  item.description ||
                  t('public_profile.family_heritage')}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PulsePreview({ posts }: { posts: PublicPulsePost[] }) {
  const { t, locale } = useTranslation();
  return (
    <div className="lr-pulse-list">
      {posts.slice(0, 3).map((post) => (
        <Link
          href={`/pulse?post=${encodeURIComponent(post.id)}`}
          className="lr-pulse-note"
          key={post.id}
        >
          <span className="lr-pulse-mark" aria-hidden>
            <MessageCircle size={15} />
          </span>
          <span>
            {post.body && <q>{storyExcerpt(post.body, 210)}</q>}
            <span className="lr-card-meta">
              {post.pillar_tag && <span>{post.pillar_tag}</span>}
              <span>{displayDate(post.created_at, locale)}</span>
              {post.witness_count > 0 && (
                <span>
                  <HeartHandshake size={12} /> {t('public_profile.witnessed_records')}
                </span>
              )}
            </span>
          </span>
        </Link>
      ))}
    </div>
  );
}

export default function LivingRecordProfile({
  data,
  routeHandle,
  isOwnerView,
  viewAsPublic,
  viewerSignedIn,
  authLoading,
  isFollowing,
  followLoading,
  copied,
  publicVaultId,
  publicVaultHref,
  onFollow,
  onMessage,
  onShare,
  onTogglePublicView,
  onRequestSection,
  loadingSection = null,
}: Props) {
  const { t, locale } = useTranslation();
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  const [timelineFilter, setTimelineFilter] = useState<'all' | TimelineKind | 'verified'>('all');
  const [mediaFilter, setMediaFilter] = useState<'photo' | 'video'>('photo');
  const [activeMedia, setActiveMedia] = useState<MediaItem | null>(null);
  const [compactIdentityVisible, setCompactIdentityVisible] = useState(false);
  const [activeSoundtrackId, setActiveSoundtrackId] = useState<string | null>(null);
  const heroRef = useRef<HTMLElement>(null);
  const mediaDialogRef = useRef<HTMLDivElement>(null);
  const mediaCloseButtonRef = useRef<HTMLButtonElement>(null);
  const mediaReturnFocusRef = useRef<HTMLElement | null>(null);
  const p = data.profile;
  const displayHandle = data.handle || routeHandle;
  const name = p.full_name || displayHandle;
  const timeline = useMemo(() => buildTimeline(data), [data]);
  const timelinePreview = useMemo(() => selectTimelinePreview(timeline), [timeline]);
  const media = useMemo(() => buildMedia(data), [data]);
  const filteredTimeline = timeline.filter(
    (item) =>
      timelineFilter === 'all' ||
      (timelineFilter === 'verified'
        ? item.verified || item.witnessed
        : item.kind === timelineFilter)
  );
  const filteredMedia = media.filter((item) => item.type === mediaFilter);
  const soundtrackItems = data.soundtrack || [];
  const journeyCount = data.stats.publicJourneyMoments ?? data.journeyMoments.length;
  const canOpenFullJourney = data.visibility.journey_map_visibility === 'full';
  const isVerified = ['witnessed', 'institution_verified', 'legacy_verified'].includes(
    data.verificationLevel
  );
  const ownerControlsVisible = isOwnerView && !viewAsPublic;
  const visibleTabs = useMemo(
    () =>
      TAB_ORDER.filter((tab) => {
        if (ownerControlsVisible || tab === 'overview' || tab === 'about') return true;
        if (tab === 'timeline') return timeline.length > 0;
        if (tab === 'stories') return data.stories.length > 0;
        if (tab === 'media') return media.length > 0;
        if (tab === 'achievements') return data.records.length > 0 || data.proofCards.length > 0;
        if (tab === 'journey') return data.journeyMoments.length > 0;
        return true;
      }),
    [
      data.journeyMoments.length,
      data.proofCards.length,
      data.records.length,
      data.stories.length,
      media.length,
      ownerControlsVisible,
      timeline.length,
    ]
  );
  const highlightStory = data.featuredStory || meaningfulStory(data.stories);
  const highlightRecord = data.featuredRecord || meaningfulRecord(data.records);
  const highlightProof = !highlightRecord ? data.proofCards[0] || null : null;
  const highlightJourney =
    [...data.journeyMoments].sort((a, b) => {
      const score = (moment: (typeof data.journeyMoments)[number]) =>
        (moment.thumbnail_url ? 4 : 0) + (moment.summary ? 2 : 0) + (moment.location_name ? 1 : 0);
      return score(b) - score(a);
    })[0] || null;
  const journeyRibbonContent = (
    <>
      {highlightJourney?.thumbnail_url && (
        <img src={highlightJourney.thumbnail_url} alt="" loading="lazy" />
      )}
      <span className="lr-journey-ribbon-copy">
        <span className="lr-journey-route">
          {data.journeyMoments
            .map((moment) => moment.location_name)
            .filter((place): place is string => Boolean(place))
            .filter((place, index, places) => places.indexOf(place) === index)
            .slice(0, 4)
            .map((place, index) => (
              <span key={place}>
                {index > 0 && <ChevronRight size={13} aria-hidden />}
                {place}
              </span>
            ))}
        </span>
        <strong>{highlightJourney?.title}</strong>
        {highlightJourney?.summary && <span>{storyExcerpt(highlightJourney.summary, 170)}</span>}
      </span>
    </>
  );
  const emphasis = profileEmphasis(data);
  const prominentStats = [
    { value: data.stats.publicStories, label: t('public_profile.records_stories') },
    { value: data.stats.publicRecords, label: t('public_profile.records_achievements') },
    { value: data.stats.verifiedRecords, label: t('public_profile.records_verified') },
    { value: journeyCount, label: t('public_profile.records_places') },
  ]
    .filter((stat) => Number(stat.value) > 0)
    .slice(0, 3);
  const highlights = [
    highlightStory
      ? {
          id: `story-${highlightStory.id}`,
          kind: t('public_profile.story'),
          title: highlightStory.title || t('public_profile.untitled_story'),
          body: highlightStory.body,
          media: highlightStory.media_url,
          target: 'stories' as ProfileTab,
        }
      : null,
    highlightRecord
      ? {
          id: `record-${highlightRecord.id}`,
          kind: t('public_profile.achievement'),
          title: highlightRecord.title,
          body: highlightRecord.description,
          media: highlightRecord.media[0]?.file_url || null,
          target: 'achievements' as ProfileTab,
        }
      : null,
    highlightProof
      ? {
          id: `proof-${highlightProof.id}`,
          kind: t('public_profile.recognition'),
          title: highlightProof.title,
          body: highlightProof.issuer_name,
          media: highlightProof.thumbnail_url,
          target: 'achievements' as ProfileTab,
        }
      : null,
    highlightJourney
      ? {
          id: `journey-${highlightJourney.id}`,
          kind: t('public_profile.journey_moment'),
          title: highlightJourney.title,
          body: highlightJourney.summary,
          media: highlightJourney.thumbnail_url,
          target: 'journey' as ProfileTab,
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));

  const tabLabels: Record<ProfileTab, string> = {
    overview: t('public_profile.overview'),
    timeline: t('public_profile.timeline'),
    stories: t('public_profile.stories'),
    media: t('public_profile.media'),
    achievements: t('public_profile.achievements'),
    journey: t('public_profile.journey'),
    about: t('public_profile.about'),
  };

  const switchTab = (tab: ProfileTab, smooth = false) => {
    setActiveTab(tab);
    onRequestSection?.(tab);
    if (typeof window !== 'undefined')
      window.requestAnimationFrame(() =>
        document
          .getElementById('living-record-tabs')
          ?.scrollIntoView({ behavior: requestedScrollBehavior(smooth), block: 'start' })
      );
  };

  const openMedia = (item: MediaItem) => {
    mediaReturnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setActiveMedia(item);
  };

  const closeMedia = () => setActiveMedia(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setCompactIdentityVisible(!entry.isIntersecting),
      { rootMargin: '-44px 0px 0px', threshold: 0 }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!activeMedia) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => mediaCloseButtonRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMedia();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        mediaDialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], video[controls], [tabindex]:not([tabindex="-1"])'
        ) || []
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      mediaReturnFocusRef.current?.focus();
    };
  }, [activeMedia]);

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab('overview');
  }, [activeTab, visibleTabs]);

  const jumpToYear = (year: string, smooth: boolean) => {
    document.getElementById(`lr-year-${year}`)?.scrollIntoView({
      behavior: requestedScrollBehavior(smooth),
      block: 'center',
    });
  };

  const timelineYears = [...new Set(filteredTimeline.map((item) => yearOf(item.date)))].filter(
    (year) => year !== '—'
  );

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tab: ProfileTab) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = visibleTabs.indexOf(tab);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? visibleTabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + visibleTabs.length) %
            visibleTabs.length;
    const nextTab = visibleTabs[nextIndex];
    if (!nextTab) return;
    setActiveTab(nextTab);
    onRequestSection?.(nextTab);
    document.getElementById(`lr-tab-${nextTab}`)?.focus();
  };

  const overviewSections = [
    data.stories.length > 0 || ownerControlsVisible
      ? {
          id: 'stories',
          priority: emphasis === 'stories' ? 10 : 30,
          content: (
            <section className="lr-section lr-editorial-stories">
              <SectionHeading
                title={t('public_profile.stories')}
                action={
                  data.stories.length > 2 ? (
                    <button
                      type="button"
                      className="lr-text-link"
                      onClick={() => switchTab('stories')}
                    >
                      {t('public_profile.view_all')} <ChevronRight size={14} />
                    </button>
                  ) : undefined
                }
              />
              <StoryGrid stories={data.stories.slice(0, 3)} emptyName={name} />
            </section>
          ),
        }
      : null,
    timeline.length > 0 || ownerControlsVisible
      ? {
          id: 'timeline',
          priority: emphasis === 'balanced' ? 10 : 20,
          content: (
            <section className="lr-section lr-editorial-timeline">
              <SectionHeading
                eyebrow={t('public_profile.life_in_time')}
                title={t('public_profile.living_timeline')}
                action={
                  timeline.length > 4 ? (
                    <button
                      type="button"
                      className="lr-text-link"
                      onClick={() => switchTab('timeline')}
                    >
                      {t('public_profile.explore_timeline')} <ChevronRight size={14} />
                    </button>
                  ) : undefined
                }
              />
              {timeline.length > 0 ? (
                <TimelineList items={timelinePreview} compact />
              ) : (
                <EmptyState
                  icon={Clock3}
                  title={t('public_profile.no_timeline')}
                  body={t('public_profile.no_timeline_body')}
                />
              )}
            </section>
          ),
        }
      : null,
    data.records.length > 0 || data.proofCards.length > 0
      ? {
          id: 'recognition',
          priority: emphasis === 'recognition' ? 10 : 40,
          content: (
            <section className="lr-section lr-editorial-recognition">
              <SectionHeading
                title={t('public_profile.recognition')}
                action={
                  <button
                    type="button"
                    className="lr-text-link"
                    onClick={() => switchTab('achievements')}
                  >
                    {t('public_profile.view_all')} <ChevronRight size={14} />
                  </button>
                }
              />
              <AchievementGrid
                records={data.records.slice(0, 3)}
                proofCards={data.proofCards.slice(0, 3)}
                emptyName={name}
              />
            </section>
          ),
        }
      : null,
    data.pulseHighlights.length > 0
      ? {
          id: 'pulse',
          priority: 35,
          content: (
            <section className="lr-section lr-editorial-pulse">
              <SectionHeading title={t('sidebar.pulse')} />
              <PulsePreview posts={data.pulseHighlights} />
            </section>
          ),
        }
      : null,
    data.journeyMoments.length > 0
      ? {
          id: 'journey',
          priority: emphasis === 'journey' ? 10 : 50,
          content: (
            <section className="lr-section lr-editorial-journey">
              <SectionHeading
                title={t('public_profile.journey')}
                action={
                  <button
                    type="button"
                    className="lr-text-link"
                    onClick={() => switchTab('journey')}
                  >
                    {t('public_profile.explore_journey')} <ChevronRight size={14} />
                  </button>
                }
              />
              {canOpenFullJourney ? (
                <Link
                  href={`/u/${encodeURIComponent(displayHandle)}/journey`}
                  className="lr-journey-ribbon"
                >
                  {journeyRibbonContent}
                </Link>
              ) : (
                <button
                  type="button"
                  className="lr-journey-ribbon"
                  onClick={() => switchTab('journey')}
                >
                  {journeyRibbonContent}
                </button>
              )}
            </section>
          ),
        }
      : null,
    data.familyItems.length > 0
      ? {
          id: 'family',
          priority: emphasis === 'family' ? 10 : 60,
          content: (
            <section className="lr-section lr-editorial-family">
              <SectionHeading title={t('public_profile.family_heritage')} />
              <FamilyPreview items={data.familyItems} name={name} />
            </section>
          ),
        }
      : null,
  ]
    .filter((section): section is NonNullable<typeof section> => Boolean(section))
    .sort((a, b) => a.priority - b.priority);
  const showOverviewRail =
    data.circles.length > 0 ||
    Boolean(publicVaultId) ||
    data.records.length > 0 ||
    data.proofCards.length > 0;
  const visitorOverviewIsEmpty =
    !ownerControlsVisible &&
    highlights.length === 0 &&
    soundtrackItems.length === 0 &&
    overviewSections.length === 0;

  return (
    <main className="lr-shell">
      <style jsx global>{`
        html:has(.lr-shell),
        body:has(.lr-shell) {
          overflow-x: clip;
        }
        .lr-shell {
          --ink: #172028;
          --muted: #65737f;
          --green: #155f52;
          --line: #dfe5e2;
          --paper: #fffefa;
          min-height: 100vh;
          overflow: visible;
          color: var(--ink);
          background: linear-gradient(180deg, #f6f8f5 0, #fbfaf6 42%, #f4f7f4 100%);
          font-family: 'DM Sans', sans-serif;
        }
        .lr-shell * {
          box-sizing: border-box;
        }
        .lr-topbar {
          min-height: 44px;
          padding: 8px max(16px, calc((100vw - 1120px) / 2));
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: #dce8e4;
          background: #13201d;
          font-size: 12px;
        }
        .lr-topbar a {
          color: inherit;
          text-decoration: none;
          font-weight: 700;
        }
        .lr-topbar-actions,
        .lr-actions,
        .lr-card-meta,
        .lr-trust-row,
        .lr-evidence-row,
        .lr-filter-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }
        .lr-container {
          width: min(1120px, 100%);
          margin: 0 auto;
          padding: 24px 24px 72px;
        }
        .lr-hero {
          overflow: hidden;
          border: 1px solid rgba(65, 79, 72, 0.18);
          border-radius: 24px;
          background: rgba(255, 254, 250, 0.94);
          box-shadow: 0 24px 70px rgba(39, 55, 48, 0.09);
        }
        .lr-cover {
          position: relative;
          height: 236px;
          background:
            radial-gradient(circle at 16% 30%, rgba(115, 185, 165, 0.48), transparent 36%),
            radial-gradient(circle at 82% 12%, rgba(211, 177, 114, 0.42), transparent 38%),
            linear-gradient(135deg, #dcebe5, #eee6d8 53%, #d7e5df);
        }
        .lr-cover img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lr-cover:after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 42%, rgba(19, 32, 29, 0.2));
        }
        .lr-identity {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          gap: 22px;
          align-items: end;
          padding: 0 30px 26px;
        }
        .lr-avatar {
          width: 116px;
          height: 116px;
          margin-top: -54px;
          position: relative;
          z-index: 1;
          border: 4px solid var(--paper);
          border-radius: 36px;
          overflow: hidden;
          display: grid;
          place-items: center;
          color: #21443b;
          background: #dfece6;
          box-shadow: 0 10px 26px rgba(28, 48, 40, 0.18);
          font:
            700 34px Georgia,
            serif;
        }
        .lr-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lr-name-block {
          min-width: 0;
          padding-top: 20px;
        }
        .lr-name-line {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 9px;
        }
        .lr-name-line h1 {
          margin: 0;
          font:
            400 clamp(30px, 4vw, 46px)/1 Georgia,
            serif;
          letter-spacing: -0.035em;
        }
        .lr-verified {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 8px;
          border-radius: 999px;
          color: #146b5c;
          background: #e3f4ef;
          font-size: 11px;
          font-weight: 800;
        }
        .lr-identifiers {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 7px 12px;
          margin-top: 9px;
          color: var(--muted);
          font-size: 13px;
        }
        .lr-identifiers .lr-vault-id {
          font:
            600 12px ui-monospace,
            monospace;
          color: #3e554e;
        }
        .lr-bio {
          max-width: 660px;
          margin: 13px 0 0;
          color: #485852;
          font-size: 15px;
          line-height: 1.65;
        }
        .lr-soundtrack-wrap {
          width: min(100%, 620px);
          margin-top: 14px;
        }
        .lr-soundtrack-chip {
          width: 100%;
          min-width: 0;
          min-height: 52px;
          padding: 4px 4px 4px 8px;
          border: 1px solid rgba(21, 95, 82, 0.2);
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 9px;
          background: linear-gradient(135deg, rgba(232, 245, 239, 0.82), rgba(255, 252, 244, 0.92));
          box-shadow: 0 5px 18px rgba(34, 65, 55, 0.07);
        }
        .lr-soundtrack-icon,
        .lr-soundtrack-row-icon {
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          color: #155f52;
          background: #dcefe8;
        }
        .lr-soundtrack-icon {
          width: 34px;
          height: 34px;
          border-radius: 11px;
        }
        .lr-soundtrack-copy,
        .lr-soundtrack-row-copy {
          min-width: 0;
          flex: 1 1 auto;
        }
        .lr-soundtrack-copy {
          display: grid;
          gap: 1px;
        }
        .lr-soundtrack-label {
          color: #467066;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.09em;
          line-height: 1.2;
          text-transform: uppercase;
        }
        .lr-soundtrack-title,
        .lr-soundtrack-artist,
        .lr-soundtrack-row-copy h3,
        .lr-soundtrack-row-copy p {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .lr-soundtrack-title {
          color: #203a33;
          font-size: 13px;
          font-weight: 800;
        }
        .lr-soundtrack-artist {
          color: #687a74;
          font-size: 11px;
        }
        .lr-soundtrack-source {
          flex: 0 0 auto;
          padding: 3px 7px;
          border: 1px solid currentColor;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.03em;
          opacity: 0.84;
        }
        .lr-soundtrack-play,
        .lr-soundtrack-edit,
        .lr-soundtrack-owner-cta {
          flex: 0 0 auto;
          min-width: 44px;
          min-height: 44px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #155f52;
          text-decoration: none;
          cursor: pointer;
          transition:
            transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
            color 150ms ease,
            background-color 150ms ease,
            border-color 150ms ease;
        }
        .lr-soundtrack-play {
          border: 1px solid #155f52;
          background: #fff;
        }
        .lr-soundtrack-play[aria-expanded='true'] {
          color: #fff;
          background: #155f52;
        }
        .lr-soundtrack-edit {
          min-width: auto;
          min-height: 40px;
          padding: 0 10px;
          gap: 5px;
          border: 0;
          font-size: 11px;
          font-weight: 800;
        }
        .lr-soundtrack-owner-cta {
          width: fit-content;
          margin-top: 14px;
          padding: 0 13px;
          gap: 7px;
          border: 1px dashed rgba(21, 95, 82, 0.35);
          background: rgba(235, 245, 240, 0.62);
          font-size: 12px;
          font-weight: 800;
        }
        .lr-soundtrack-player {
          width: 100%;
          margin-top: 8px;
          overflow: hidden;
          border: 1px solid rgba(21, 95, 82, 0.16);
          border-radius: 13px;
          background: #f5f8f6;
        }
        .lr-soundtrack-player iframe,
        .lr-soundtrack-player audio {
          width: 100%;
          max-width: 100%;
          display: block;
          border: 0;
        }
        .lr-soundtrack-player audio {
          min-height: 44px;
        }
        .lr-soundtrack-list {
          display: grid;
          gap: 9px;
        }
        .lr-soundtrack-row {
          min-width: 0;
          padding: 9px;
          border: 1px solid var(--line);
          border-radius: 15px;
          background: rgba(255, 254, 250, 0.9);
        }
        .lr-soundtrack-row-main {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .lr-soundtrack-row-icon {
          width: 36px;
          height: 36px;
          border-radius: 11px;
        }
        .lr-soundtrack-row-copy h3 {
          margin: 0;
          color: #20332d;
          font-size: 13px;
        }
        .lr-soundtrack-row-copy p {
          margin: 3px 0 0;
          color: var(--muted);
          font-size: 11px;
        }
        .lr-soundtrack-play:focus-visible,
        .lr-soundtrack-edit:focus-visible,
        .lr-soundtrack-owner-cta:focus-visible {
          outline: 3px solid rgba(21, 95, 82, 0.28);
          outline-offset: 2px;
        }
        .lr-soundtrack-play:active,
        .lr-soundtrack-edit:active,
        .lr-soundtrack-owner-cta:active {
          transform: scale(0.97);
        }
        .lr-actions {
          justify-content: flex-end;
          padding-bottom: 2px;
        }
        .lr-button {
          min-height: 42px;
          padding: 0 15px;
          border: 1px solid #cfd9d5;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          color: #21443b;
          background: #fff;
          font:
            700 13px 'DM Sans',
            sans-serif;
          text-decoration: none;
          cursor: pointer;
          transition:
            transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
            background-color 150ms ease,
            border-color 150ms ease;
        }
        .lr-button:active {
          transform: scale(0.97);
        }
        .lr-button-primary {
          border-color: #155f52;
          color: #fff;
          background: #155f52;
        }
        .lr-record-strip {
          display: flex;
          gap: 18px;
          padding: 11px 30px 15px;
          border-top: 1px solid var(--line);
          background: rgba(251, 252, 249, 0.72);
        }
        .lr-stat strong {
          display: inline;
          font:
            600 14px 'DM Sans',
            sans-serif;
          color: #1c302a;
        }
        .lr-stat span {
          margin-left: 5px;
          color: var(--muted);
          font-size: 12px;
          font-weight: 600;
        }
        .lr-tabs-wrap {
          position: sticky;
          top: 0;
          z-index: 20;
          margin-top: 18px;
          border: 1px solid var(--line);
          border-radius: 15px;
          background: rgba(250, 251, 247, 0.92);
          backdrop-filter: blur(16px);
          box-shadow: 0 8px 24px rgba(37, 50, 44, 0.06);
          display: flex;
          align-items: center;
        }
        .lr-compact-identity {
          width: 0;
          opacity: 0;
          overflow: hidden;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          color: #243d35;
          font-size: 12px;
          font-weight: 800;
          pointer-events: none;
          transform: translateX(-6px);
          transition:
            width 180ms cubic-bezier(0.23, 1, 0.32, 1),
            opacity 140ms ease,
            transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        .lr-compact-identity[data-visible='true'] {
          width: 170px;
          padding-left: 8px;
          opacity: 1;
          transform: translateX(0);
        }
        .lr-compact-avatar {
          width: 28px;
          height: 28px;
          flex: 0 0 28px;
          overflow: hidden;
          border-radius: 9px;
          display: grid;
          place-items: center;
          color: #fff;
          background: #274f45;
          font-size: 9px;
        }
        .lr-compact-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lr-tabs {
          display: flex;
          flex: 1;
          min-width: 0;
          overflow-x: auto;
          scrollbar-width: none;
          padding: 5px;
          gap: 2px;
          scroll-snap-type: x proximity;
        }
        .lr-tabs::-webkit-scrollbar {
          display: none;
        }
        .lr-tab {
          min-height: 42px;
          padding: 0 15px;
          flex: 0 0 auto;
          scroll-snap-align: start;
          border: 0;
          border-radius: 10px;
          color: #68766f;
          background: transparent;
          font:
            700 13px 'DM Sans',
            sans-serif;
          cursor: pointer;
          transition:
            color 150ms ease,
            background-color 150ms ease,
            transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        .lr-tab:active {
          transform: scale(0.97);
        }
        .lr-tab[aria-selected='true'] {
          color: #174f45;
          background: #e7efeb;
        }
        .lr-button:focus-visible,
        .lr-tab:focus-visible,
        .lr-filter:focus-visible,
        .lr-text-link:focus-visible {
          outline: 3px solid rgba(28, 132, 114, 0.28);
          outline-offset: 2px;
        }
        .lr-panel {
          padding-top: 34px;
        }
        .lr-loading-note {
          width: fit-content;
          margin: -12px 0 16px;
          padding: 5px 9px;
          border-radius: 999px;
          color: #4f655d;
          background: #e9efec;
          font-size: 11px;
          font-weight: 700;
        }
        .lr-overview-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.65fr) minmax(270px, 0.75fr);
          gap: 34px;
          align-items: start;
        }
        .lr-overview-grid-single {
          grid-template-columns: minmax(0, 1fr);
        }
        .lr-overview-grid-single .lr-overview-main {
          width: min(900px, 100%);
        }
        .lr-overview-main {
          min-width: 0;
        }
        .lr-rail {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .lr-section {
          margin-bottom: 42px;
          scroll-margin-top: 82px;
        }
        .lr-section-heading {
          margin-bottom: 17px;
          display: flex;
          align-items: end;
          justify-content: space-between;
          gap: 16px;
        }
        .lr-eyebrow {
          margin: 0 0 6px;
          color: #34776a;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .lr-section-heading h2 {
          margin: 0;
          font:
            400 26px/1.15 Georgia,
            serif;
          letter-spacing: -0.02em;
        }
        .lr-section-description {
          max-width: 640px;
          margin: 7px 0 0;
          color: var(--muted);
          font-size: 13px;
          line-height: 1.55;
        }
        .lr-text-link {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          border: 0;
          padding: 0;
          color: #176a5b;
          background: none;
          font-weight: 800;
          font-size: 12px;
          text-decoration: none;
          cursor: pointer;
        }
        .lr-highlights {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(220px, 0.65fr);
          grid-template-rows: repeat(2, minmax(150px, 1fr));
          gap: 12px;
        }
        .lr-highlights-1 {
          grid-template-columns: minmax(0, 1fr);
          grid-template-rows: minmax(310px, auto);
        }
        .lr-highlights-2 {
          grid-template-columns: minmax(0, 1.35fr) minmax(220px, 0.65fr);
          grid-template-rows: minmax(310px, auto);
        }
        .lr-highlight {
          min-height: 170px;
          position: relative;
          overflow: hidden;
          border: 1px solid #d9dfdc;
          border-radius: 18px;
          color: #fff;
          background: #31443d;
          text-align: left;
          cursor: pointer;
          box-shadow: 0 10px 30px rgba(29, 47, 39, 0.11);
          transition:
            transform 180ms cubic-bezier(0.23, 1, 0.32, 1),
            box-shadow 180ms ease;
        }
        .lr-highlights:not(.lr-highlights-1):not(.lr-highlights-2) .lr-highlight:first-child {
          min-height: 354px;
          grid-row: 1 / span 2;
        }
        .lr-highlights-2 .lr-highlight:first-child {
          min-height: 310px;
        }
        .lr-highlights-2 .lr-highlight:last-child {
          min-height: 250px;
        }
        .lr-highlight:active {
          transform: scale(0.985);
        }
        .lr-highlight img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lr-highlight:after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(10, 25, 20, 0.04) 25%, rgba(10, 23, 19, 0.91));
        }
        .lr-highlight-copy {
          position: absolute;
          z-index: 1;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 18px;
        }
        .lr-highlight-copy span {
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #b9ded3;
        }
        .lr-highlight-copy h3 {
          margin: 6px 0 0;
          font:
            400 20px/1.2 Georgia,
            serif;
        }
        .lr-highlight-copy p {
          margin: 7px 0 0;
          color: #dce7e3;
          font-size: 12px;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .lr-highlight-placeholder {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 22% 22%, rgba(134, 197, 179, 0.5), transparent 38%),
            linear-gradient(145deg, #405c52, #263b34);
        }
        .lr-timeline {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .lr-timeline-item {
          display: grid;
          grid-template-columns: 54px 18px minmax(0, 1fr);
          gap: 10px;
          align-items: stretch;
        }
        .lr-timeline-year {
          padding-top: 18px;
          color: #42584f;
          font:
            600 13px ui-monospace,
            monospace;
          text-align: right;
        }
        .lr-timeline-line {
          position: relative;
          display: flex;
          justify-content: center;
        }
        .lr-timeline-line:before {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          width: 1px;
          background: #cad7d1;
        }
        .lr-timeline-line span {
          width: 9px;
          height: 9px;
          position: relative;
          margin-top: 23px;
          border: 2px solid #f8faf6;
          border-radius: 50%;
          background: #39796c;
          box-shadow: 0 0 0 1px #39796c;
        }
        .lr-timeline-card {
          min-height: 104px;
          margin-bottom: 12px;
          overflow: hidden;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          border: 1px solid var(--line);
          border-radius: 15px;
          background: var(--paper);
        }
        .lr-timeline-copy {
          padding: 16px;
          min-width: 0;
        }
        .lr-card-meta {
          color: #78847f;
          font-size: 11px;
        }
        .lr-card-meta > span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .lr-kind {
          padding: 4px 7px;
          border-radius: 999px;
          font-weight: 800;
          text-transform: capitalize;
        }
        .lr-kind-neutral {
          color: #4e625a;
          background: #edf1ef;
        }
        .lr-timeline-card h3,
        .lr-story-card h3,
        .lr-achievement-card h3 {
          margin: 9px 0 0;
          font:
            400 19px/1.25 Georgia,
            serif;
        }
        .lr-timeline-card p,
        .lr-achievement-card > p {
          margin: 7px 0 0;
          color: #596760;
          font-size: 13px;
          line-height: 1.55;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .lr-timeline-media {
          width: 128px;
          min-height: 104px;
        }
        .lr-timeline-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lr-trust-row,
        .lr-evidence-row {
          margin-top: 10px;
          color: #357366;
          font-size: 11px;
          font-weight: 800;
        }
        .lr-trust-row span,
        .lr-evidence-row span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .lr-rail-card {
          padding: 20px 2px 24px;
          border-top: 1px solid var(--line);
          background: transparent;
        }
        .lr-rail-card h3 {
          margin: 10px 0 6px;
          font:
            400 20px Georgia,
            serif;
        }
        .lr-rail-card > p {
          margin: 0;
          color: var(--muted);
          font-size: 12.5px;
          line-height: 1.55;
        }
        .lr-circle-names {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }
        .lr-circle-names span {
          padding: 6px 9px;
          border-radius: 999px;
          color: #53675f;
          background: #edf2ef;
          font-size: 11px;
          font-weight: 700;
        }
        .lr-rail-icon {
          width: 38px;
          height: 38px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          color: #176a5b;
          background: #e4f2ed;
        }
        .lr-places {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin: 14px 0;
        }
        .lr-place {
          padding: 6px 8px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          border: 1px solid #d7e2de;
          border-radius: 9px;
          color: #496158;
          background: #f9fbf8;
          font-size: 11px;
          font-weight: 700;
        }
        .lr-trust-list {
          margin: 14px 0 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 9px;
        }
        .lr-trust-list li {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #3e554e;
          font-size: 12px;
        }
        .lr-filter-row {
          margin-bottom: 18px;
        }
        .lr-filter {
          min-height: 34px;
          padding: 0 11px;
          border: 1px solid #d7dfdb;
          border-radius: 999px;
          color: #68766f;
          background: #fff;
          font:
            700 11px 'DM Sans',
            sans-serif;
          cursor: pointer;
        }
        .lr-filter[aria-pressed='true'] {
          color: #fff;
          border-color: #1c6759;
          background: #1c6759;
        }
        .lr-year-nav {
          margin: 4px 0 18px;
          padding: 7px 0;
          display: flex;
          gap: 14px;
          overflow-x: auto;
          border-bottom: 1px solid var(--line);
          scrollbar-width: none;
        }
        .lr-year-nav::-webkit-scrollbar {
          display: none;
        }
        .lr-year-nav button {
          flex: 0 0 auto;
          border: 0;
          padding: 5px 0;
          color: #4b6259;
          background: transparent;
          font:
            700 12px ui-monospace,
            monospace;
          cursor: pointer;
        }
        .lr-year-nav button:focus-visible {
          outline: 3px solid rgba(28, 132, 114, 0.28);
          outline-offset: 2px;
        }
        .lr-story-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        .lr-story-card {
          overflow: hidden;
          border-top: 1px solid var(--line);
          background: transparent;
        }
        .lr-story-featured {
          grid-column: 1/-1;
          display: grid;
          grid-template-columns: minmax(260px, 0.85fr) minmax(0, 1.15fr);
          border: 0;
          border-radius: 0;
        }
        .lr-story-featured-text {
          display: block;
          max-width: 760px;
        }
        .lr-story-featured-text .lr-story-copy {
          padding-left: 0;
        }
        .lr-story-media {
          min-height: 210px;
          overflow: hidden;
          border-radius: 20px;
          background: #e8ece9;
        }
        .lr-story-media img,
        .lr-story-media video {
          width: 100%;
          height: 100%;
          min-height: 210px;
          display: block;
          object-fit: cover;
        }
        .lr-story-copy {
          padding: 20px 4px;
        }
        .lr-story-featured .lr-story-copy {
          padding: 24px 0 24px 32px;
        }
        .lr-story-copy h3 {
          font-size: 23px;
        }
        .lr-story-copy > p {
          margin: 10px 0 15px;
          color: #53615b;
          font-size: 13.5px;
          line-height: 1.65;
          display: -webkit-box;
          -webkit-line-clamp: 5;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .lr-story-copy blockquote {
          margin: 16px 0 20px;
          color: #33483f;
          font:
            400 clamp(19px, 2vw, 27px) / 1.48 Georgia,
            serif;
        }
        .lr-pulse-list {
          display: grid;
          gap: 0;
          border-top: 1px solid var(--line);
        }
        .lr-pulse-note {
          padding: 17px 2px;
          display: grid;
          grid-template-columns: 34px minmax(0, 1fr);
          gap: 12px;
          border-bottom: 1px solid var(--line);
          color: inherit;
          text-decoration: none;
        }
        .lr-pulse-mark {
          width: 30px;
          height: 30px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          color: #176a5b;
          background: #e4f2ed;
        }
        .lr-pulse-note q {
          display: block;
          margin-bottom: 9px;
          color: #344a42;
          font:
            400 16px/1.55 Georgia,
            serif;
        }
        .lr-pulse-note:hover q {
          color: #155f52;
        }
        .lr-media-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }
        .lr-media-card {
          aspect-ratio: 1;
          position: relative;
          overflow: hidden;
          border: 1px solid var(--line);
          padding: 0;
          border-radius: 14px;
          color: #fff;
          background: #dfe5e2;
          text-decoration: none;
          cursor: pointer;
        }
        .lr-media-card img,
        .lr-media-card video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lr-media-overlay {
          position: absolute;
          inset: auto 0 0;
          padding: 32px 12px 12px;
          background: linear-gradient(transparent, rgba(10, 20, 17, 0.84));
        }
        .lr-media-overlay strong {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 12px;
        }
        .lr-media-overlay span {
          margin-top: 3px;
          display: flex;
          align-items: center;
          gap: 4px;
          color: #d5e2dd;
          font-size: 10px;
          text-transform: capitalize;
        }
        .lr-play {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: rgba(12, 25, 21, 0.68);
        }
        .lr-media-dialog {
          position: fixed;
          inset: 0;
          z-index: 80;
          display: grid;
          place-items: center;
          padding: 24px;
          background: rgba(13, 21, 18, 0.88);
          backdrop-filter: blur(12px);
        }
        .lr-media-dialog-card {
          width: min(920px, 100%);
          max-height: min(86vh, 820px);
          overflow: hidden;
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(220px, 300px);
          border-radius: 22px;
          background: #fffefa;
          box-shadow: 0 28px 90px rgba(0, 0, 0, 0.36);
        }
        .lr-media-dialog-visual {
          min-height: 420px;
          display: grid;
          place-items: center;
          background: #101916;
        }
        .lr-media-dialog-visual img,
        .lr-media-dialog-visual video {
          width: 100%;
          height: 100%;
          max-height: 78vh;
          object-fit: contain;
        }
        .lr-media-dialog-copy {
          padding: 70px 26px 26px;
        }
        .lr-media-dialog-copy h2 {
          margin: 8px 0 12px;
          font:
            400 25px/1.25 Georgia,
            serif;
        }
        .lr-media-close {
          position: absolute;
          z-index: 2;
          top: 14px;
          right: 14px;
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border: 1px solid #d8dfdb;
          border-radius: 50%;
          color: #243d35;
          background: rgba(255, 254, 250, 0.92);
          cursor: pointer;
          transition: transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        .lr-media-close:active {
          transform: scale(0.97);
        }
        .lr-achievement-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }
        .lr-recognition-stack {
          display: grid;
          gap: 22px;
        }
        .lr-achievement-card {
          padding: 20px 20px 20px 24px;
          border-left: 2px solid #d7b768;
          background: linear-gradient(90deg, rgba(255, 247, 222, 0.68), transparent 74%);
        }
        .lr-achievement-icon {
          width: 42px;
          height: 42px;
          margin-bottom: 14px;
          display: grid;
          place-items: center;
          border-radius: 13px;
          color: #916019;
          background: #fff3d5;
        }
        .lr-trust-badge {
          padding: 4px 7px;
          border-radius: 999px;
          font-weight: 800;
        }
        .lr-trust-institution,
        .lr-trust-verified {
          color: #126758;
          background: #dff3ed;
        }
        .lr-trust-witnessed {
          color: #6251a0;
          background: #eeeafd;
        }
        .lr-trust-evidence {
          color: #805b1b;
          background: #fff3d9;
        }
        .lr-trust-personal {
          color: #596760;
          background: #edf1ef;
        }
        .lr-achievement-byline {
          color: #3f574e !important;
          font-weight: 700;
        }
        .lr-proof-list {
          display: grid;
          gap: 0;
          border-top: 1px solid var(--line);
        }
        .lr-proof-row {
          padding: 16px 2px;
          display: grid;
          grid-template-columns: 48px minmax(0, 1fr);
          gap: 14px;
          align-items: center;
          border-bottom: 1px solid var(--line);
        }
        .lr-proof-row > img,
        .lr-proof-icon {
          width: 46px;
          height: 46px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          color: #176a5b;
          background: #e4f2ed;
          object-fit: cover;
        }
        .lr-proof-row h3 {
          margin: 7px 0 3px;
          font-size: 16px;
        }
        .lr-proof-row p {
          margin: 0;
          color: var(--muted);
          font-size: 12px;
        }
        .lr-journey-layout {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(250px, 0.65fr);
          gap: 20px;
        }
        .lr-journey-ribbon {
          width: 100%;
          min-height: 240px;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
          padding: 28px;
          border-radius: 22px;
          color: #fff;
          background:
            radial-gradient(circle at 22% 32%, rgba(134, 210, 190, 0.4), transparent 26%),
            linear-gradient(135deg, #173b34, #38695d);
          text-decoration: none;
          border: 0;
          text-align: left;
          cursor: pointer;
          font: inherit;
        }
        .lr-journey-ribbon > img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.4;
        }
        .lr-journey-ribbon:after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(8, 27, 22, 0.05), rgba(8, 27, 22, 0.88));
        }
        .lr-journey-ribbon-copy {
          position: relative;
          z-index: 1;
          display: grid;
          gap: 8px;
        }
        .lr-journey-ribbon-copy > strong {
          font:
            400 clamp(24px, 3vw, 34px) / 1.18 Georgia,
            serif;
        }
        .lr-journey-ribbon-copy > span:last-child {
          max-width: 620px;
          color: rgba(255, 255, 255, 0.78);
          font-size: 13px;
          line-height: 1.55;
        }
        .lr-journey-route {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px;
          color: #e5f5f0;
          font-size: 11px;
          font-weight: 800;
        }
        .lr-journey-route > span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .lr-map-card {
          min-height: 270px;
          position: relative;
          overflow: hidden;
          border: 1px solid #cad9d3;
          border-radius: 20px;
          background:
            radial-gradient(circle at 24% 38%, rgba(54, 140, 121, 0.27) 0 4px, transparent 5px),
            radial-gradient(circle at 68% 63%, rgba(54, 140, 121, 0.3) 0 4px, transparent 5px),
            linear-gradient(145deg, #dcebe5, #edf0e5);
        }
        .lr-map-card:before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0.32;
          background-image:
            linear-gradient(rgba(36, 92, 78, 0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(36, 92, 78, 0.12) 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .lr-map-card-copy {
          position: absolute;
          left: 18px;
          right: 18px;
          bottom: 18px;
          padding: 16px;
          border: 1px solid rgba(255, 255, 255, 0.7);
          border-radius: 15px;
          background: rgba(255, 255, 255, 0.84);
          backdrop-filter: blur(12px);
        }
        .lr-map-card-copy h3 {
          margin: 0;
          font:
            400 20px Georgia,
            serif;
        }
        .lr-map-card-copy p {
          margin: 5px 0 0;
          color: var(--muted);
          font-size: 12px;
        }
        .lr-journey-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .lr-journey-stop {
          padding: 12px 2px 14px;
          border-bottom: 1px solid var(--line);
          background: transparent;
        }
        .lr-journey-stop h3 {
          margin: 0;
          font:
            400 17px Georgia,
            serif;
        }
        .lr-journey-stop p {
          margin: 5px 0 0;
          color: var(--muted);
          font-size: 12px;
        }
        .lr-about-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.25fr) minmax(260px, 0.75fr);
          gap: 20px;
        }
        .lr-about-card {
          padding: 24px;
          border: 1px solid var(--line);
          border-radius: 18px;
          background: var(--paper);
        }
        .lr-about-card:only-child {
          grid-column: 1 / -1;
          max-width: 760px;
        }
        .lr-about-card h2 {
          margin: 0 0 12px;
          font:
            400 24px Georgia,
            serif;
        }
        .lr-about-card > p {
          margin: 0;
          color: #4f6058;
          font-size: 14px;
          line-height: 1.7;
        }
        .lr-detail-list {
          margin: 18px 0 0;
          display: grid;
          gap: 10px;
        }
        .lr-detail {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #506159;
          font-size: 13px;
        }
        .lr-family-list {
          display: flex;
          flex-direction: column;
          gap: 9px;
        }
        .lr-family-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px;
          border: 1px solid #e2e7e4;
          border-radius: 12px;
          background: #fbfcfa;
        }
        .lr-family-item-featured {
          align-items: flex-end;
          min-height: 118px;
          padding: 14px;
          position: relative;
          overflow: hidden;
          border: 0;
          color: #fff;
          background: linear-gradient(145deg, #6b4a55, #ad6b78);
        }
        .lr-family-item-featured > img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border-radius: 0;
          opacity: 0.48;
        }
        .lr-family-item-featured:after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(transparent 20%, rgba(49, 22, 33, 0.84));
        }
        .lr-family-item-featured > div {
          position: relative;
          z-index: 1;
        }
        .lr-family-item-featured h4,
        .lr-family-item-featured p {
          color: #fff;
        }
        .lr-family-item > img,
        .lr-family-item > span {
          width: 38px;
          height: 38px;
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          border-radius: 10px;
          color: #9a4768;
          background: #f7e8ef;
          object-fit: cover;
        }
        .lr-family-item h4 {
          margin: 0;
          font-size: 12px;
        }
        .lr-family-item p,
        .lr-muted-copy {
          margin: 3px 0 0;
          color: var(--muted);
          font-size: 11px;
          line-height: 1.45;
        }
        .lr-empty {
          min-height: 150px;
          padding: 24px;
          display: flex;
          align-items: flex-start;
          gap: 13px;
          border: 1px dashed #cbd6d1;
          border-radius: 18px;
          background: rgba(255, 254, 250, 0.58);
        }
        .lr-empty-icon {
          width: 42px;
          height: 42px;
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          border-radius: 13px;
          color: #357366;
          background: #e5f0ec;
        }
        .lr-empty h3 {
          margin: 3px 0 5px;
          font:
            400 18px Georgia,
            serif;
        }
        .lr-empty p {
          margin: 0;
          color: var(--muted);
          font-size: 13px;
          line-height: 1.55;
        }
        @media (hover: hover) and (pointer: fine) {
          .lr-highlight:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 34px rgba(29, 47, 39, 0.16);
          }
          .lr-button:hover {
            border-color: #9eb8ae;
            background: #f7faf8;
          }
          .lr-button-primary:hover {
            border-color: #104f44;
            background: #104f44;
          }
          .lr-soundtrack-play:hover,
          .lr-soundtrack-edit:hover,
          .lr-soundtrack-owner-cta:hover {
            border-color: #0f5146;
            background: #edf6f2;
          }
          .lr-soundtrack-play[aria-expanded='true']:hover {
            color: #fff;
            background: #0f5146;
          }
        }
        @media (max-width: 800px) {
          .lr-container {
            padding: 12px 12px 56px;
          }
          .lr-cover {
            height: 166px;
          }
          .lr-identity {
            grid-template-columns: auto minmax(0, 1fr);
            padding: 0 17px 20px;
            gap: 14px;
          }
          .lr-avatar {
            width: 94px;
            height: 94px;
            margin-top: -38px;
            border-radius: 28px;
          }
          .lr-name-block {
            padding-top: 13px;
          }
          .lr-actions {
            grid-column: 1/-1;
            width: 100%;
            justify-content: stretch;
          }
          .lr-actions .lr-button {
            flex: 1;
          }
          .lr-record-strip {
            padding: 14px 17px;
            gap: 20px;
            overflow-x: auto;
          }
          .lr-stat {
            flex: 0 0 auto;
          }
          .lr-tabs-wrap {
            margin-top: 12px;
          }
          .lr-panel {
            padding-top: 26px;
          }
          .lr-overview-grid,
          .lr-journey-layout,
          .lr-about-grid {
            grid-template-columns: minmax(0, 1fr);
          }
          .lr-rail {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .lr-highlights {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            margin-right: -12px;
            padding-right: 12px;
          }
          .lr-highlight {
            min-width: min(78vw, 300px);
            min-height: 260px !important;
            scroll-snap-align: start;
          }
          .lr-story-grid,
          .lr-achievement-grid {
            grid-template-columns: minmax(0, 1fr);
          }
          .lr-story-featured {
            grid-column: auto;
            display: block;
          }
          .lr-media-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .lr-section {
            margin-bottom: 34px;
          }
          .lr-topbar {
            padding: 8px 12px;
          }
          .lr-topbar-label {
            display: none;
          }
          .lr-compact-identity {
            display: none;
          }
          .lr-media-dialog-card {
            max-height: 88vh;
            overflow-y: auto;
            grid-template-columns: minmax(0, 1fr);
          }
          .lr-media-dialog-visual {
            min-height: 280px;
          }
          .lr-media-dialog-copy {
            padding: 20px;
          }
          .lr-journey-ribbon {
            min-height: 220px;
            padding: 20px;
          }
        }
        @media (max-width: 520px) {
          .lr-cover {
            height: 132px;
          }
          .lr-identity {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }
          .lr-avatar {
            width: 88px;
            height: 88px;
            margin-top: -42px;
          }
          .lr-name-block {
            padding-top: 0;
            width: 100%;
          }
          .lr-name-line h1 {
            font-size: 30px;
          }
          .lr-actions {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 6px;
          }
          .lr-actions .lr-button {
            min-width: 0;
            padding: 0 8px;
            font-size: 11px;
          }
          .lr-soundtrack-source {
            display: none;
          }
          .lr-soundtrack-edit {
            width: 44px;
            min-width: 44px;
            padding: 0;
          }
          .lr-soundtrack-edit span {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
          .lr-record-strip {
            gap: 18px;
          }
          .lr-rail {
            grid-template-columns: minmax(0, 1fr);
          }
          .lr-timeline-item {
            grid-template-columns: 42px 14px minmax(0, 1fr);
            gap: 6px;
          }
          .lr-timeline-year {
            font-size: 11px;
          }
          .lr-timeline-card {
            grid-template-columns: minmax(0, 1fr);
          }
          .lr-timeline-media {
            width: 100%;
            height: 138px;
            grid-row: 1;
          }
          .lr-timeline-copy {
            grid-row: 2;
          }
          .lr-section-heading {
            align-items: flex-start;
          }
          .lr-media-grid {
            gap: 7px;
          }
          .lr-hero {
            border-radius: 18px;
          }
          .lr-story-copy {
            padding: 17px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .lr-button,
          .lr-tab,
          .lr-highlight,
          .lr-compact-identity,
          .lr-media-close,
          .lr-soundtrack-play,
          .lr-soundtrack-edit,
          .lr-soundtrack-owner-cta {
            transition:
              color 120ms ease,
              background-color 120ms ease,
              border-color 120ms ease;
          }
          .lr-highlight:hover,
          .lr-button:active,
          .lr-tab:active,
          .lr-media-close:active,
          .lr-soundtrack-play:active,
          .lr-soundtrack-edit:active,
          .lr-soundtrack-owner-cta:active {
            transform: none;
          }
        }
      `}</style>

      {isOwnerView && (
        <div className="lr-topbar">
          <Link href="/individual-dashboard">← {t('public_profile.back_dashboard')}</Link>
          <span className="lr-topbar-label">
            {viewAsPublic ? t('public_profile.public_preview') : t('public_profile.owner_view')}
          </span>
          <div className="lr-topbar-actions">
            <button className="lr-button" type="button" onClick={onTogglePublicView}>
              <CircleUserRound size={13} />{' '}
              {viewAsPublic ? t('public_profile.owner_view') : t('public_profile.view_as_public')}
            </button>
          </div>
        </div>
      )}
      {!isOwnerView && viewerSignedIn && (
        <div className="lr-topbar">
          <Link href="/individual-dashboard">← {t('public_profile.my_ledger')}</Link>
          <span className="lr-topbar-label">{t('public_profile.public_living_record')}</span>
        </div>
      )}
      {!viewerSignedIn && !authLoading && !isOwnerView && (
        <div className="lr-topbar">
          <span className="lr-topbar-label">{t('public_profile.public_living_record')}</span>
          <div className="lr-topbar-actions">
            <Link href="/login">{t('nav.login')}</Link>
            <Link href="/register">{t('nav.register')}</Link>
          </div>
        </div>
      )}

      <div className="lr-container">
        <header className="lr-hero" ref={heroRef} data-emphasis={emphasis}>
          <div className="lr-cover">{p.banner_url && <img src={p.banner_url} alt="" />}</div>
          <div className="lr-identity">
            <div className="lr-avatar">
              {p.avatar_url ? <img src={p.avatar_url} alt={`${name} profile`} /> : initials(name)}
            </div>
            <div className="lr-name-block">
              <div className="lr-name-line">
                <h1>{name}</h1>
                {isVerified && (
                  <span className="lr-verified">
                    <ShieldCheck size={13} /> {t('public_profile.verified_identity')}
                  </span>
                )}
              </div>
              <div className="lr-identifiers">
                <span>@{displayHandle}</span>
                {publicVaultId && <span className="lr-vault-id">{publicVaultId}</span>}
                {p.location && (
                  <span>
                    <MapPin size={12} /> {p.location}
                  </span>
                )}
                {p.created_at && (
                  <span>
                    <Clock3 size={12} /> {yearOf(p.created_at)}
                  </span>
                )}
              </div>
              {p.bio && <p className="lr-bio">{p.bio}</p>}
              <PublicCurrentRole handle={displayHandle} />
              <LifeSoundtrackHeader
                items={soundtrackItems}
                isOwner={ownerControlsVisible}
                soundtrackState={data.soundtrackState}
                activeTrackId={activeSoundtrackId}
                onActiveTrackChange={setActiveSoundtrackId}
              />
            </div>
            <div className="lr-actions">
              {ownerControlsVisible ? (
                <>
                  <Link
                    href="/individual-dashboard/public-identity"
                    className="lr-button lr-button-primary"
                  >
                    <Pencil size={14} /> {t('public_profile.edit_profile')}
                  </Link>
                  <button type="button" className="lr-button" onClick={onShare}>
                    {copied ? <Check size={14} /> : <Share2 size={14} />}{' '}
                    {copied ? t('public_profile.copied') : t('public_profile.share')}
                  </button>
                  {publicVaultId && data.visibility.allow_vault_on_profile && (
                    <Link href={publicVaultHref} className="lr-button">
                      <ShieldCheck size={14} /> {t('public_profile.public_vault')}
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {!authLoading && (
                    <button
                      type="button"
                      className="lr-button lr-button-primary"
                      onClick={viewAsPublic && isOwnerView ? undefined : onFollow}
                      disabled={followLoading}
                      aria-disabled={viewAsPublic && isOwnerView}
                    >
                      {isFollowing ? <UserCheck size={14} /> : <UserPlus size={14} />}{' '}
                      {isFollowing ? t('public_profile.following') : t('public_profile.follow')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="lr-button"
                    onClick={viewAsPublic && isOwnerView ? undefined : onMessage}
                    aria-disabled={viewAsPublic && isOwnerView}
                  >
                    <MessageCircle size={14} /> {t('public_profile.message')}
                  </button>
                  <button type="button" className="lr-button" onClick={onShare}>
                    {copied ? <Check size={14} /> : <Share2 size={14} />}{' '}
                    {copied ? t('public_profile.copied') : t('public_profile.share')}
                  </button>
                </>
              )}
            </div>
          </div>
          {prominentStats.length > 0 && (
            <div className="lr-record-strip" aria-label={t('public_profile.trust_public_vault')}>
              {prominentStats.map((stat) => (
                <div className="lr-stat" key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          )}
        </header>

        <nav
          id="living-record-tabs"
          className="lr-tabs-wrap"
          aria-label={t('public_profile.profile_sections')}
        >
          <div
            className="lr-compact-identity"
            data-visible={compactIdentityVisible ? 'true' : 'false'}
            aria-hidden={!compactIdentityVisible}
          >
            <span className="lr-compact-avatar">
              {p.avatar_url ? <img src={p.avatar_url} alt="" /> : initials(name)}
            </span>
            <span>{name}</span>
          </div>
          <div className="lr-tabs" role="tablist" aria-label={t('public_profile.profile_sections')}>
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`lr-tab-${tab}`}
                aria-selected={activeTab === tab}
                aria-controls={`lr-panel-${tab}`}
                tabIndex={activeTab === tab ? 0 : -1}
                className="lr-tab"
                onClick={(event) => switchTab(tab, event.detail > 0)}
                onKeyDown={(event) => handleTabKeyDown(event, tab)}
              >
                {tabLabels[tab]}
              </button>
            ))}
          </div>
        </nav>

        <div
          id={`lr-panel-${activeTab}`}
          className="lr-panel"
          role="tabpanel"
          aria-labelledby={`lr-tab-${activeTab}`}
        >
          {loadingSection === activeTab && activeTab !== 'overview' && (
            <div className="lr-loading-note" role="status">
              {t('common.loading')}
            </div>
          )}
          {activeTab === 'overview' && (
            <div
              className={`lr-overview-grid ${showOverviewRail ? '' : 'lr-overview-grid-single'}`}
            >
              <div className="lr-overview-main">
                {visitorOverviewIsEmpty && (
                  <section className="lr-section lr-empty-profile">
                    <EmptyState
                      icon={BookOpen}
                      title={t('empty_states.no_stories')}
                      body={t('public_profile.no_stories_body')}
                    />
                  </section>
                )}
                {(highlights.length > 0 || ownerControlsVisible) && (
                  <section className="lr-section">
                    <SectionHeading title={t('public_profile.highlights')} />
                    {highlights.length > 0 ? (
                      <div
                        className={`lr-highlights lr-highlights-${Math.min(highlights.length, 3)}`}
                      >
                        {highlights.map((item) => (
                          <button
                            type="button"
                            className="lr-highlight"
                            key={item.id}
                            onClick={(event) => switchTab(item.target, event.detail > 0)}
                          >
                            {item.media ? (
                              <img src={item.media} alt="" loading="lazy" />
                            ) : (
                              <span className="lr-highlight-placeholder" />
                            )}
                            <span className="lr-highlight-copy">
                              <span>{item.kind}</span>
                              <h3>{item.title}</h3>
                              {item.body && <p>{storyExcerpt(item.body, 170)}</p>}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <EmptyState
                        icon={Sparkles}
                        title={t('public_profile.no_highlights')}
                        body={t('public_profile.no_highlights_body')}
                      />
                    )}
                  </section>
                )}
                <LifeSoundtrackCollection
                  items={soundtrackItems}
                  activeTrackId={activeSoundtrackId}
                  onActiveTrackChange={setActiveSoundtrackId}
                />
                {overviewSections.map((section) => (
                  <div key={section.id}>{section.content}</div>
                ))}
              </div>
              {showOverviewRail && (
                <aside className="lr-rail" aria-label={t('public_profile.profile_context')}>
                  {data.circles.length > 0 && (
                    <section className="lr-rail-card">
                      <span className="lr-rail-icon">
                        <Users size={19} />
                      </span>
                      <h3>{t('public_profile.people_circles')}</h3>
                      <div className="lr-circle-names">
                        {data.circles.slice(0, 3).map((circle) => (
                          <span key={circle.id}>{circle.name}</span>
                        ))}
                      </div>
                    </section>
                  )}
                  {(publicVaultId || data.records.length > 0 || data.proofCards.length > 0) && (
                    <section className="lr-rail-card">
                      <span className="lr-rail-icon">
                        <ShieldCheck size={19} />
                      </span>
                      <h3>{t('public_profile.trust_public_vault')}</h3>
                      <ul className="lr-trust-list">
                        {data.stats.verifiedRecords > 0 && (
                          <li>
                            <ShieldCheck size={14} /> {data.stats.verifiedRecords}{' '}
                            {t('public_profile.verified_records')}
                          </li>
                        )}
                        {data.records.some((r) => r.witness_state === 'witnessed') && (
                          <li>
                            <HeartHandshake size={14} /> {t('public_profile.witnessed_records')}
                          </li>
                        )}
                        {data.records.some((r) => r.evidence_state === 'added') && (
                          <li>
                            <FileCheck2 size={14} /> {t('public_profile.evidence_attached')}
                          </li>
                        )}
                        {publicVaultId && (
                          <li>
                            <FileCheck2 size={14} /> {publicVaultId}
                          </li>
                        )}
                        {data.proofCards.length > 0 && (
                          <li>
                            <ShieldCheck size={14} /> {data.stats.publicProofCards}{' '}
                            {t('dashboard.trust_record')}
                          </li>
                        )}
                      </ul>
                      {publicVaultId && data.visibility.allow_vault_on_profile && (
                        <Link
                          href={publicVaultHref}
                          className="lr-text-link"
                          style={{ marginTop: 14 }}
                        >
                          {t('public_profile.open_public_vault')} <ExternalLink size={13} />
                        </Link>
                      )}
                    </section>
                  )}
                </aside>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <section className="lr-section">
              <SectionHeading
                eyebrow={t('public_profile.life_in_time')}
                title={t('public_profile.living_timeline')}
              />
              <div className="lr-filter-row" aria-label={t('public_profile.timeline_filters')}>
                {(['all', 'story', 'achievement', 'journey', 'family', 'verified'] as const).map(
                  (filter) => (
                    <button
                      key={filter}
                      type="button"
                      className="lr-filter"
                      aria-pressed={timelineFilter === filter}
                      onClick={() => setTimelineFilter(filter)}
                    >
                      {t(`public_profile.filter_${filter}`)}
                    </button>
                  )
                )}
              </div>
              {timelineFilter === 'all' && <PublicCareerAndChapters handle={displayHandle} />}
              {timelineYears.length > 1 && (
                <nav className="lr-year-nav" aria-label={t('public_profile.life_in_time')}>
                  {timelineYears.map((year) => (
                    <button
                      type="button"
                      key={year}
                      onClick={(event) => jumpToYear(year, event.detail > 0)}
                    >
                      {year}
                    </button>
                  ))}
                </nav>
              )}
              {filteredTimeline.length > 0 ? (
                <TimelineList items={filteredTimeline} />
              ) : (
                <EmptyState
                  icon={Clock3}
                  title={t('public_profile.no_timeline_matches')}
                  body={t('public_profile.no_timeline_matches_body')}
                />
              )}
            </section>
          )}
          {activeTab === 'stories' && (
            <section className="lr-section">
              <SectionHeading title={t('public_profile.stories')} />
              <StoryGrid stories={data.stories} emptyName={name} />
            </section>
          )}
          {activeTab === 'media' && (
            <section className="lr-section">
              <SectionHeading title={t('public_profile.media')} />
              <div
                className="lr-filter-row"
                role="group"
                aria-label={t('public_profile.media_filters')}
              >
                <button
                  type="button"
                  className="lr-filter"
                  aria-pressed={mediaFilter === 'photo'}
                  onClick={() => setMediaFilter('photo')}
                >
                  <ImageIcon size={13} /> {t('public_profile.photos')}
                </button>
                <button
                  type="button"
                  className="lr-filter"
                  aria-pressed={mediaFilter === 'video'}
                  onClick={() => setMediaFilter('video')}
                >
                  <Film size={13} /> {t('public_profile.videos')}
                </button>
              </div>
              {filteredMedia.length > 0 ? (
                <div className="lr-media-grid">
                  {filteredMedia.map((item) => (
                    <button
                      type="button"
                      className="lr-media-card"
                      key={item.id}
                      onClick={() => openMedia(item)}
                      aria-label={`${item.title} · ${t(
                        item.context === 'story'
                          ? 'public_profile.story'
                          : 'public_profile.achievement'
                      )}`}
                    >
                      {item.type === 'video' ? (
                        <video src={item.url} preload="metadata" aria-label={item.title} />
                      ) : (
                        <img src={item.url} alt={item.title} loading="lazy" />
                      )}
                      {item.type === 'video' && (
                        <span className="lr-play">
                          <Play size={14} fill="white" />
                        </span>
                      )}
                      <span className="lr-media-overlay">
                        <strong>{item.title || t('public_profile.untitled_story')}</strong>
                        <span>
                          {item.context === 'story' ? <BookOpen size={11} /> : <Award size={11} />}
                          {t(
                            item.context === 'story'
                              ? 'public_profile.story'
                              : 'public_profile.achievement'
                          )}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={mediaFilter === 'photo' ? ImageIcon : Film}
                  title={t(
                    mediaFilter === 'photo'
                      ? 'public_profile.no_photos'
                      : 'public_profile.no_videos'
                  )}
                  body={t('public_profile.no_media_body')}
                />
              )}
            </section>
          )}
          {activeTab === 'achievements' && (
            <section className="lr-section">
              <SectionHeading title={t('public_profile.achievements_recognition')} />
              <AchievementGrid
                records={data.records}
                proofCards={data.proofCards}
                emptyName={name}
              />
            </section>
          )}
          {activeTab === 'journey' && (
            <section className="lr-section">
              <SectionHeading title={t('public_profile.journey')} />
              {data.journeyMoments.length > 0 ? (
                <div className="lr-journey-layout">
                  <div className="lr-map-card">
                    <div className="lr-map-card-copy">
                      <h3>
                        {journeyCount} {t('public_profile.meaningful_places')}
                      </h3>
                      <p>
                        {data.journeyMoments
                          .map((m) => m.location_name)
                          .filter(Boolean)
                          .slice(0, 3)
                          .join(' · ')}
                      </p>
                      {canOpenFullJourney && (
                        <Link
                          href={`/u/${encodeURIComponent(displayHandle)}/journey`}
                          className="lr-text-link"
                          style={{ marginTop: 10 }}
                        >
                          {t('public_profile.open_full_journey')} <ExternalLink size={13} />
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="lr-journey-list">
                    {data.journeyMoments.map((moment) => (
                      <article className="lr-journey-stop" key={moment.id}>
                        <h3>{moment.title}</h3>
                        <p>
                          {[displayDate(moment.date, locale), moment.location_name]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        {moment.summary && <p>{moment.summary}</p>}
                      </article>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={MapPin}
                  title={t('public_profile.no_journey')}
                  body={t('public_profile.no_journey_body')}
                />
              )}
            </section>
          )}
          {activeTab === 'about' && (
            <section className="lr-section">
              <SectionHeading title={t('public_profile.about')} />
              <div className="lr-about-grid">
                <article className="lr-about-card">
                  <h2>{t('public_profile.about_name').replace('{{name}}', name)}</h2>
                  <p>{p.bio || t('public_profile.no_bio')}</p>
                  <div className="lr-detail-list">
                    <span className="lr-detail">
                      <CircleUserRound size={15} /> @{displayHandle}
                    </span>
                    {publicVaultId && (
                      <span className="lr-detail">
                        <ShieldCheck size={15} /> {publicVaultId}
                      </span>
                    )}
                    {p.location && (
                      <span className="lr-detail">
                        <MapPin size={15} /> {p.location}
                      </span>
                    )}
                    {p.created_at && (
                      <span className="lr-detail">
                        <Calendar size={15} /> {t('public_profile.member_since')}{' '}
                        {displayDate(p.created_at, locale)}
                      </span>
                    )}
                  </div>
                </article>
                {(data.familyItems.length > 0 || data.circles.length > 0) && (
                  <article className="lr-about-card">
                    {data.familyItems.length > 0 && (
                      <>
                        <h2>{t('public_profile.family_heritage')}</h2>
                        <FamilyPreview items={data.familyItems} name={name} />
                      </>
                    )}
                    {data.circles.length > 0 && (
                      <>
                        <h2 style={{ marginTop: data.familyItems.length > 0 ? 24 : 0 }}>
                          {t('public_profile.people_circles')}
                        </h2>
                        <p>
                          {data.circles
                            .slice(0, 3)
                            .map((circle) => circle.name)
                            .join(' · ')}
                        </p>
                      </>
                    )}
                  </article>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
      {activeMedia && (
        <div
          className="lr-media-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lr-media-dialog-title"
          onClick={closeMedia}
        >
          <div
            className="lr-media-dialog-card"
            ref={mediaDialogRef}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="lr-media-close"
              ref={mediaCloseButtonRef}
              onClick={closeMedia}
              aria-label={t('common.close')}
            >
              <X size={18} aria-hidden />
            </button>
            <div className="lr-media-dialog-visual">
              {activeMedia.type === 'video' ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video src={activeMedia.url} controls preload="metadata" />
              ) : (
                <img src={activeMedia.url} alt={activeMedia.title} />
              )}
            </div>
            <div className="lr-media-dialog-copy">
              <span className="lr-kind lr-kind-neutral">
                {activeMedia.context === 'story' ? <BookOpen size={12} /> : <Award size={12} />}
                {t(
                  activeMedia.context === 'story'
                    ? 'public_profile.story'
                    : 'public_profile.achievement'
                )}
              </span>
              <h2 id="lr-media-dialog-title">
                {activeMedia.title || t('public_profile.untitled_story')}
              </h2>
              {activeMedia.context === 'story' ? (
                <Link className="lr-text-link" href={activeMedia.href}>
                  {t('trust.view_story')} <ChevronRight size={14} />
                </Link>
              ) : (
                <button
                  type="button"
                  className="lr-text-link"
                  onClick={() => {
                    setActiveMedia(null);
                    switchTab('achievements');
                  }}
                >
                  {t('public_profile.achievements')} <ChevronRight size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
